import React, { useCallback, useState } from 'react';
import { Stack, Button, Text } from '@moda/om';
import { ShippingMethodType } from '../../../constants';
import { ShippingMethod } from '../../../hooks/useCart/cart.rest';
import { FormButtonsLayout } from '../../FormButtonsLayout';
import { ShippingMethodOptionList } from '../ShippingMethodOptionList';
import { SameDayDeliveryFields } from '../SameDayDeliveryFields';

import './ShippingMethodForm.scss';

const MIN_PHONE_LENGTH = 7;

const getSameDayDeliveryMessage = (transitTime: string): string => {
  if (transitTime === 'Delivered Today') {
    return 'Order before 11 AM and your order will deliver by 6 PM today';
  }
  return 'Orders placed after 11AM will deliver the next business day by 6PM';
};

export interface SameDayData {
  phone: string;
  sameDayDeliveryNotes: string;
}

interface Props {
  shippingOptions: ShippingMethod[];
  // TODO: move to constants as enums
  selectedOption: number;
  onSelectOption: (selectedOption: number) => void;
  onCancel: () => void;
  onSubmit: (method: number, sameDayData?: SameDayData) => void;
  initialPhone?: string;
  initialDeliveryNotes?: string;
}

export const ShippingMethodForm: React.FC<Props> = ({
  shippingOptions,
  selectedOption,
  onSelectOption,
  onCancel,
  onSubmit,
  initialPhone = '',
  initialDeliveryNotes = ''
}) => {
  const [phone, setPhone] = useState(initialPhone);
  const [deliveryNotes, setDeliveryNotes] = useState(initialDeliveryNotes);
  const [phoneError, setPhoneError] = useState<string>();

  const isSameDayDelivery = selectedOption === ShippingMethodType.SameDay;

  const validatePhone = useCallback(() => {
    if (isSameDayDelivery && (!phone || phone.trim().length < MIN_PHONE_LENGTH)) {
      setPhoneError('Phone number is required for same-day delivery');
      return false;
    }
    setPhoneError(undefined);
    return true;
  }, [isSameDayDelivery, phone]);

  const handleSubmit = useCallback(() => {
    if (isSameDayDelivery) {
      if (!validatePhone()) return;
      onSubmit(selectedOption, { phone, sameDayDeliveryNotes: deliveryNotes });
    } else {
      onSubmit(selectedOption);
    }
  }, [isSameDayDelivery, validatePhone, onSubmit, selectedOption, phone, deliveryNotes]);

  return (
    <Stack className="ShippingMethodForm" space={4}>
      <ShippingMethodOptionList
        shippingOptions={shippingOptions}
        selectedOption={selectedOption}
        onSelectOption={onSelectOption}
      />

      <Text treatment="bold1">
        {isSameDayDelivery
          ? getSameDayDeliveryMessage(
              shippingOptions.find(opt => opt.method === ShippingMethodType.SameDay)?.transitTime ??
                ''
            )
          : 'Orders placed after 11AM EST will be processed the following business day.'}
      </Text>

      {isSameDayDelivery && (
        <SameDayDeliveryFields
          phone={phone}
          onPhoneChange={setPhone}
          phoneError={phoneError}
          deliveryNotes={deliveryNotes}
          onDeliveryNotesChange={setDeliveryNotes}
        />
      )}

      <FormButtonsLayout>
        <Button data-test-id="shipping-method--cancel" onClick={onCancel} secondary>
          Cancel
        </Button>
        <Button data-test-id="shipping-method--continue" onClick={handleSubmit} type="submit">
          Continue
        </Button>
      </FormButtonsLayout>
    </Stack>
  );
};
